import React from 'react';
import { withRouter } from 'react-router-dom';
import { get, isEmpty, head, find, isNil, isUndefined } from 'lodash';
import queryString from 'query-string';
import searchQuery from 'query-string';

// local dependencies
import { rxEnableAutoRefill as RxEnableAutoRefillView } from 'ui/views/widgets/rxEnableAutoRefill';
import { RxEnableAutoRefillService } from 'services/rxEnableAutoRefill';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, ERROR_RESPONSE_TEMPLATE, SIZES, ORIGIN } from 'constants/common';
import { DASHBOARD, DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { GLOBAL_LABELS } from 'constants/common';
import { getLabel } from 'utils/getLabel';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { ATTESTATION_PROVIDER } from 'constants/common';
import { getAppSyncData } from 'utils/getAppSyncData';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { RxEnableAutoRefill } from 'ui/containers/RxEnableAutoRefill';
 *
 * // render
 * <RxEnableAutoRefill propName='propValue'/>
 */
class _RxEnableAutoRefill extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind functions
        this._bind();

        // new service instance
        this.rxEnableAutoRefillService = new RxEnableAutoRefillService();

        // get query paramaters from url
        const queryParams = queryString.parse( this.props.location.search );

        // get `memberId`, `rxId` and `origin` from query parameters
        const memberId = isUndefined( this.props.location.state ) ? getAppSyncData( 'memberId' ) : get( this.props.location, 'state.memberId', undefined );
        const patientId = isUndefined( this.props.location.state ) ? getAppSyncData( 'patientId' ) : get( this.props.location, 'state.patientId', undefined );
        const rxId = get( queryParams, 'rxId' );
        const origin = get( queryParams, 'origin' );

        // construct render redirection path for add payment
        const addPaymentRedirectPath = queryString.stringifyUrl( {
            url: this.props.location.pathname,
            query: {
                rxId
            }
        } );

        // set initial state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,

            patientId,
            memberId,
            rxId,
            origin,
            addPaymentRedirectPath,

            userConsent: false,
            attestationProvider: null,
            firstName: '',
            lastName: '',
            enableSubmit: false,

            consentStatus: '',
            patientStartDate: '',
            patientEndDate: '',
            originalConsentStatus: '',
        };
        
        // if - `memberId` or `rxId` is not present, redirect to the dashboard landing
        // else - make service request for the component data
        if ( isEmpty( this.state.memberId ) || isEmpty( this.state.rxId ) ) {
            this.props.history.replace( DASHBOARD );
        } else {
            this.fetchComponentData(); // start component data fetch
        }
    }

    /**
     * @desc Bind functions
     */
    _bind() {
        this.handleCheckboxClick = this.handleCheckboxClick.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleAddressAdd = this.handleAddressAdd.bind( this );
        this.handleAttestationProviderChange = this.handleAttestationProviderChange.bind( this );
        this.handleNameFieldChange = this.handleNameFieldChange.bind( this );
        this.handleFetchData = this.handleFetchData.bind( this );
        this.handleConsentStatusChange = this.handleConsentStatusChange.bind( this );
        this.getCaliforniaConsentValidation = this.getCaliforniaConsentValidation.bind( this );

    }

    getCaliforniaConsentValidation() {
        const visibleItems = get( this.state.componentData, 'visibleItems', [] );
        const patientAutorefillConsentSection = find( visibleItems, { name: 'patientAutorefillConsentSection' } );
        const isCaliforniaUser = get( this.state.componentData, 'data.state.isCaliforniaUser', false );
        const consentStatus = this.state.consentStatus;
        
        const isConsentRequired = isCaliforniaUser && patientAutorefillConsentSection;
        const isConsentInvalid = isConsentRequired && ( isNil( consentStatus ) || isEmpty( consentStatus ) ||  consentStatus === 'No Record' );
        
        return { isConsentRequired, isConsentInvalid };
    }


    /**
     * @desc Fetch component data
     */
    async fetchComponentData() {
        log.debug( 'RxEnableAutoRefill.fetchComponentData()' );

        // cancel ongoing request
        this.rxEnableAutoRefillService.cancel();

        try {
            const { status, data }  = await this.rxEnableAutoRefillService.fetchComponentData( {
                url: this.props.widget.url,
                data: {
                    context: {
                        memberId: this.state.memberId,
                        patientId:this.state.patientId
                    },
                    data: {
                        rxId: this.state.rxId
                    }
                }
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                const values = get( data, 'data.state', {} );
                const addresses =  get( values, 'savedAddresses', [] );
                const paymentOptions = get( values, 'paymentOptions', [] );

                const visibleItems = get( data, 'visibleItems', [] );
                const paymentOptionsSection = find( visibleItems, { name: 'paymentOptionsSection' } );
                const patientAutorefillConsentSection = find( visibleItems, { name: 'patientAutorefillConsentSection' } );
                const isCaliforniaUser = get( values, 'isCaliforniaUser', false );
                const patientAutorefillConsent = get( values, 'patientAutorefillConsent', {} );
                const consentStatus = get( patientAutorefillConsent, 'consentStatus', null );
                const patientStartDate = get( patientAutorefillConsent, 'patientStartDate', '' );
                const patientEndDate = get( patientAutorefillConsent, 'patientEndDate', '' );

                let enableSubmit = ! isEmpty( addresses );

                if( paymentOptionsSection && isEmpty( paymentOptions ) ) {
                    enableSubmit = false;
                }

                if( get( values, 'userConsentRequired' ) && ! this.state.userConsent ) {
                    enableSubmit = false;
                }
                
                // check if submit button can be enabled
                // let enableSubmit = false;
                // if ( paymentOptionsSection ) {  // in case of a payment cag
                //     enableSubmit = ! ( isEmpty( addresses ) || isEmpty( paymentOptions ) );
                // } else {  // in case of a non-payment cag
                //     enableSubmit = ! isEmpty( addresses ) && this.state.userConsent;
                // }

                if( isCaliforniaUser && patientAutorefillConsentSection && ( isNil( consentStatus ) || isEmpty( consentStatus ) || consentStatus === 'No Record' ) ) {
                    enableSubmit = false;
                }

                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                    enableSubmit,
                    consentStatus: consentStatus || '',
                    patientStartDate,
                    patientEndDate,
                    originalConsentStatus: consentStatus || ''
                } );
            }
        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    /**
     * @desc Handles address additon
     */
    async handleAddressAdd( { address, id = null } ) {
        log.debug( 'RxEnableAutoRefill.handleAddressAdd()' );

        // get add address action
        const addAddressAction = find( get( this.state.componentData, 'visibleItems', [] ), { name: 'addAddressAction'}, {} );

        // cancel ongoing request
        this.rxEnableAutoRefillService.cancel();
        
        // show page preloader
        pageLevelPreloader.show();

        // construct form data
        const formData = {
            ...address,
            id: id,
            flag: {
                isDefault: true
            }
        };

        // get callId and callType from store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        // get submit url from add address action config
        const url = get( addAddressAction, 'config.submitUrl', null );

         // submit new address
        const { status } = await this.rxEnableAutoRefillService.submitNewAddress( {
            url,
            data: {
                context: {
                    callId,
                    callType,
                    memberId: this.state.memberId,
                    patientId: this.state.patientId,
                    operation: CRUD_OPERATION.CREATE
                },
                data: formData
            }
        } );

        // show page preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: true,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {  // fetch new data
                this.fetchComponentData();
            } );
        } else {  // show error dialog
            errorOverlay.open();
        }
    }

    /**
     * @desc Handles checkbox click
     */
    async handleCheckboxClick() {
        log.debug( 'RxEnableAutoRefill.handleCheckboxClick()' );

        // get address from state
        const savedAddresses = get( this.state.componentData, 'data.state.savedAddresses', [] );

        // check if attestation provider and name is required
        const visibleItems = get( this.state.componentData, 'visibleItems', [] );
        const isAttestationProviderRequired = find( visibleItems, { name: 'attestationProvider' } );
        const isNameRequired = ATTESTATION_PROVIDER.CLINIC === this.state.attestationProvider || 
                               ATTESTATION_PROVIDER.CAREGIVER === this.state.attestationProvider;

        const { isConsentInvalid } = this.getCaliforniaConsentValidation();

        let enableSubmit = ! isEmpty( savedAddresses ) && ! this.state.userConsent;

        if( isAttestationProviderRequired && isNil( this.state.attestationProvider ) ) {
            enableSubmit = false;
        }

        if ( isNameRequired && ( isEmpty( this.state.firstName ) || isEmpty( this.state.lastName ) ) ) {
            enableSubmit = false;
        }

         if( isConsentInvalid ) {
            enableSubmit = false;
        }

        this.setState( ( state ) => {
            return {
                userConsent: !state.userConsent,
                enableSubmit: enableSubmit
            };
        } );
    }

    /**
     * @desc Handles attestation provider change
     */
    async handleAttestationProviderChange( value ) {
        log.debug( 'RxEnableAutoRefill.handleAttestationProviderChange()' );

        // get address from state
        const savedAddresses = get( this.state.componentData, 'data.state.savedAddresses', [] );

        const isNameRequired = ATTESTATION_PROVIDER.CLINIC === value || ATTESTATION_PROVIDER.CAREGIVER === value;
        const { isConsentInvalid } = this.getCaliforniaConsentValidation();


        // check if submit button can be enabled
        let enableSubmit = ! isEmpty( savedAddresses ) && this.state.userConsent;

        if ( isNameRequired && ( isEmpty( this.state.firstName ) || isEmpty( this.state.lastName ) ) ) {
            enableSubmit = false;
        }

        if( isConsentInvalid ) {
            enableSubmit = false;
        }

        // update state of both attestation provider and submit button
        this.setState( {
            attestationProvider: value,
            enableSubmit
        } );
    }

    /**
     * @desc Handles change in any name field
     */
    async handleNameFieldChange( { name, value } ) {
        log.debug( 'RxEnableAutoRefill.handleNameFieldChange()' );

        // get address from state
        const savedAddresses = get( this.state.componentData, 'data.state.savedAddresses', [] );
        const { isConsentInvalid } = this.getCaliforniaConsentValidation();

        // if first name field is changed
        if ( 'firstName' === name ) {

            // check if submit button can be enabled
            const enableSubmit = ! isEmpty( savedAddresses ) && this.state.userConsent && ! isEmpty( value ) && ! isEmpty( this.state.lastName );
          
            if( isConsentInvalid ) {
                enableSubmit = false;
            }
            // update state of first name and submit button
            this.setState( {
                firstName: value,
                enableSubmit
            } );
        } else {  // if last name field was changed

            // check if submit button can be enabled
            const enableSubmit = ! isEmpty( savedAddresses ) && this.state.userConsent && ! isEmpty( this.state.firstName ) && ! isEmpty( value );
            
            if( isConsentInvalid ) {
                enableSubmit = false;
            }
            // update state of last name and submit button
            this.setState( {
                lastName: value,
                enableSubmit
            } );
        }
    }

     async handleConsentStatusChange( value ) {
        log.debug( 'RxEnableAutoRefill.handleConsentStatusChange()' );

        const savedAddresses = get( this.state.componentData, 'data.state.savedAddresses', [] );
        const paymentOptions = get( this.state.componentData, 'data.state.paymentOptions', [] );
        const visibleItems = get( this.state.componentData, 'visibleItems', [] );
        const paymentOptionsSection = find( visibleItems, { name: 'paymentOptionsSection' } );
        const userConsentRequired = get( this.state.componentData, 'data.state.userConsentRequired', false );

        let startDate = this.state.patientStartDate;
        let endDate = this.state.patientEndDate;

        if( 'Accepted' === value && isEmpty( startDate ) ) {
            const today = new Date();
            startDate = today.toISOString().split('T')[0];
            
            const oneYearLater = new Date(today);
            oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
            endDate = oneYearLater.toISOString().split('T')[0];
        }

        let enableSubmit = ! isEmpty( savedAddresses );

        if( paymentOptionsSection && isEmpty( paymentOptions ) ) {
            enableSubmit = false;
        }

        if( userConsentRequired && ! this.state.userConsent ) {
            enableSubmit = false;
        }

        if( isNil( value ) || isEmpty( value ) ) {
            enableSubmit = false;
        }

        this.setState( {
            consentStatus: value,
            patientStartDate: startDate,
            patientEndDate: endDate,
            enableSubmit
        } );
    }


    /**
     * @desc Handles action clicks
     */
    async handleActionClick ( { type, value } ) {
        log.debug( 'RxEnableAutoRefill.handleActionClick()' );

        // if user pressed cancel action
        if( 'navAction' === type && 'cancel' === value.name ) {

            // check origin and redirect to appropriate page
            if ( ORIGIN.MEMBER_MANAMGENT === this.state.origin ) {

                this.props.history.replace( {
                    pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                    state: {
                        entitiesActiveTabIndex: 1,
                        memberId: this.state.memberId,
                        patientId: this.state.patientId
                    }
                } );
            } else if ( ORIGIN.RX_DETAIL === this.state.origin ) {

                this.props.history.replace( {
                    pathname: DASHBOARD_SUB_ROUTES.PRESCRIPTION_DETAIL,
                    search: searchQuery.stringify( {
                        rxId: this.state.rxId
                    } ),
                    state: {  memberId: this.state.memberId,  patientId: this.state.patientId }
                } );
            }
        } else if ( 'navAction' === type && 'submit' === value.name ) {  // if user pressed submit button

            // cancel ongoing service
            this.rxEnableAutoRefillService.cancel();

            // show page preloader
            pageLevelPreloader.show();

            // get default values
            const values = get( this.state.componentData, 'data.state', [] );

            // prepare payload
            const data = {
                context: {
                    memberId: this.state.memberId,
                    patientId: this.state.patientId
                },
                data: {
                    rxId: this.state.rxId,
                    enableAuto: true,
                    userConsentRequired: get( values, 'userConsentRequired', null ),
                    userConsent: this.state.userConsent,
                    attestationProvider: this.state.attestationProvider,
                    phoneNumber: get( values, 'phoneNumber', '' ) // added phone number field
                }
            };

             const isCaliforniaUser = get( values, 'isCaliforniaUser', false );
            if( isCaliforniaUser ) {
                const patientAutorefillConsent = get( values, 'patientAutorefillConsent', {} );
                data.data.patientAutorefillConsent = {
                    consentStatus: this.state.consentStatus,
                    isAutoRefillConsentExpired: get( patientAutorefillConsent, 'isAutoRefillConsentExpired', false ),
                    originalConsentStatus: this.state.originalConsentStatus
                };
            }

            // get address and payment option from component data
            const savedAddresses = get( values, 'savedAddresses', [] );
            const paymentOptions = get( values, 'paymentOptions', [] );

            // add addressId to data if address is available
            if ( ! isEmpty( savedAddresses ) ) {
                data.data.addressId = get( head( savedAddresses ), 'id' );
            }

            // add paymentId to data if payment option is available
            if ( ! isEmpty( paymentOptions ) ) {
                data.data.paymentId = get( head( paymentOptions ), 'id' );
            }

            // add first name and last name/initials if attestation provider is not patient
            if ( ATTESTATION_PROVIDER.PATIENT !== this.state.attestationProvider ) {
                data.data.firstName = this.state.firstName;
                
                if ( ATTESTATION_PROVIDER.CLINIC === this.state.attestationProvider ) {
                    data.data.lastInitials = this.state.lastName;
                } else {
                    data.data.lastName = this.state.lastName;
                }
            }

            // submit data and get status
            const { status, _data } = await this.rxEnableAutoRefillService.enableAutoRefillSubmit( {
                url: value.url,
                data
            } );

            // hide page preloader
            pageLevelPreloader.hide();

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                // show success overlay
                successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ) } );

                // check origin and redirect user to appropriate page
                if ( ORIGIN.MEMBER_MANAMGENT === this.state.origin ) {

                    this.props.history.replace( {
                        pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                        state: {
                            entitiesActiveTabIndex: 1,
                            memberId: this.state.memberId,
                            patientId: this.state.patientId
                        }
                    } );
                } else if ( ORIGIN.RX_DETAIL === this.state.origin ) {

                    this.props.history.replace( {
                        pathname: DASHBOARD_SUB_ROUTES.PRESCRIPTION_DETAIL,
                        search: searchQuery.stringify( {
                            rxId: this.state.rxId
                        } ),
                        state: { memberId: this.state.memberId,  patientId: this.state.patientId }
                    } );
                }
            } else {

                const template = get( _data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

                if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                    errorOverlay.open( { message: _data.description, size: SIZES.SMALL } );
                } else {
                    errorOverlay.open();
                }
            }
        }
    }

    handleFetchData() {
        log.debug( 'RxEnableAutoRefill.handleFetchData()' );

        this.setState( {
            isFetching: true,
            networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
        }, () => {  // fetch new data
            this.fetchComponentData();
        } );
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <RxEnableAutoRefillView
                { ...this.state }
                handleAddressAdd={ this.handleAddressAdd }
                handleCheckboxClick={ this.handleCheckboxClick }
                handleAttestationProviderChange={ this.handleAttestationProviderChange }
                handleNameFieldChange={ this.handleNameFieldChange }
                handleActionClick={ this.handleActionClick }
                handleFetchData={ this.handleFetchData }
                handleConsentStatusChange={ this.handleConsentStatusChange }

            />
        );
    }
}

// set display name
_RxEnableAutoRefill.displayName = 'RxEnableAutoRefill';

// set default props
_RxEnableAutoRefill.defaultProps = {};

// prop types
_RxEnableAutoRefill.propTypes = {};

export const RxEnableAutoRefill = withRouter( _RxEnableAutoRefill );
